// stepEditMenuItems — the WYSIWYG step ops as universal ContextMenuItem[], so
// the right-click menu (edit mode) carries them alongside the navigate/copy
// items. This is the "fold the ⋯ step menu into the universal context menu"
// step: same ProcessStepEditApi, expressed as flat menu items. Text-carrying
// ops (add / retype / reassign) can't live inline in a flat menu, so they call
// `openPrompt` with a spec the surface's StepEditPrompt renders at the pointer.

import type { ContextMenuItem } from "@archlang/ui";
import type { NodeId } from "@archlang/engine";
import type { ProcessStepEditApi, WrapConstruct } from "./processStepEdit.js";
import type { StepPromptSpec } from "./StepEditPrompt.js";

const WRAP_CONSTRUCTS: readonly WrapConstruct[] = ["if", "each", "try", "parallel", "reversible"];

export interface StepEditTarget {
  readonly nodeId: NodeId;
  /** `call` unlocks retype-callee + reassign-owner; `block` unlocks
   *  add-step-inside; other nodes get add/delete/wrap only. */
  readonly kind: "call" | "block" | "other";
  /** Walk stepIndex — enables the owner-reassign item when reassignable. */
  readonly stepIndex?: number;
  /** Prefill for retype/reassign (`from > to`). */
  readonly from?: string;
  readonly to?: string;
}

/** The step-edit ops for one node, as context-menu items. Empty when the api is
 *  read-only. `openPrompt` receives a spec sans coords — the surface supplies
 *  the pointer position (it owns the ContextMenuEvent). */
export function stepEditMenuItems(
  api: ProcessStepEditApi,
  target: StepEditTarget,
  openPrompt: (spec: Omit<StepPromptSpec, "x" | "y">) => void,
): ContextMenuItem[] {
  if (!api.canEdit) return [];
  const { nodeId, kind, stepIndex, from, to } = target;
  const items: ContextMenuItem[] = [];

  items.push({
    label: "Add step after…",
    separator: true,
    onClick: () => openPrompt({ label: "Step (e.g. A > B)", initial: "", run: (v) => api.addStepAfter(nodeId, v) }),
  });
  items.push({
    label: "Add step before…",
    onClick: () => openPrompt({ label: "Step (e.g. A > B)", initial: "", run: (v) => api.insertBefore(nodeId, v) }),
  });
  if (kind === "block") {
    items.push({
      label: "Add step inside…",
      onClick: () => openPrompt({ label: "Step (e.g. A > B)", initial: "", run: (v) => api.insertInto(nodeId, v) }),
    });
  }
  if (kind === "call") {
    items.push({
      label: "Change callee…",
      onClick: () =>
        openPrompt({
          label: "from > to",
          initial: from && to ? `${from} > ${to}` : "",
          run: (v) => {
            const [f, t] = v.split(">").map((s) => s.trim());
            return f !== undefined && t !== undefined && api.replaceCall(nodeId, f, t);
          },
        }),
    });
    if (stepIndex !== undefined && api.canReassignOwner(stepIndex)) {
      items.push({
        label: "Reassign owner…",
        onClick: () =>
          openPrompt({ label: "New owner module", initial: from ?? "", run: (v) => api.reassignOwner(stepIndex, v.trim()) }),
      });
    }
  }

  // Wrap-in-construct — flat items (the menu has no submenu); grouped by a rule.
  WRAP_CONSTRUCTS.forEach((c, i) => {
    items.push({ label: `Wrap in ${c}`, separator: i === 0, onClick: () => void api.wrap(nodeId, c) });
  });

  items.push({ label: "Delete step", danger: true, separator: true, onClick: () => void api.deleteStep(nodeId) });
  return items;
}
